import { useQuery } from "@apollo/client";
import {
  CredentialEditorSurface,
  CredentialKind,
  InventoryOverviewQuery,
  Permission,
} from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useTenant } from "hooks/useTenant";
import { useValidatingForm } from "hooks/useValidatingForm";
import pluralize from "pluralize";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCredentialInputValidation } from "routes/Customer/Chemicals/Report/validationHooks/useCredentialInputValidation";
import { GET_CREDENTIAL } from "routes/Customer/Credentials/schema";
import { CREDENTIAL_FOR_STAFF_QUERY } from "routes/Staff/Credentials/CredentialManagement/api";

import { CredentialFacilityRow } from "./CredentialFacilitiesGrid";
import {
  CredentialForm,
  CredentialFormValues,
  CredentialSurface,
  CredentialTenancy,
  CustomerCredential,
  StaffCredential,
} from "./types";
import { staffFacilityIds, useCredentialFacilities } from "./useCredentialFacilities";
import { useCredentialWrites } from "./useCredentialWrites";

export type { CredentialSurface, StaffCredential } from "./types";

function staffDefaultValues(credential: StaffCredential | undefined): CredentialFormValues {
  return {
    id: credential?.id,
    name: credential?.name ?? "",
    username: credential?.username ?? "",
    email: credential?.email ?? "",
    password: credential?.password ?? "",
    kind: credential?.kind ?? CredentialKind.Tierii,
    portalId: credential?.portalId ?? null,
    // Only an Encamp-owned credential is allowed no tenant, and a create starts with no
    // facilities to take one from — matching `upsertCredentialForStaff`'s own default.
    isEncampOwned: credential?.isEncampOwned ?? true,
    // Kept in step with the staged grid by `useCredentialFacilities`; a reset lands back here.
    facilityIds: staffFacilityIds(credential),
    securityQuestions: credential?.securityQuestions ?? [],
  };
}

/** The fields `CredentialForStaffInput` has no column for, so only this surface sends them. */
function customerOnlyDefaults(credential: CustomerCredential | undefined) {
  return {
    usesCombinedInvoicing: credential?.usesCombinedInvoicing ?? false,
    usesRcoCredential: credential?.usesRcoCredential ?? false,
    usesEManifestApi: credential?.usesEManifestApi ?? false,
    securityQuestions: credential?.securityQuestions ?? [],
    isEncampOwned: credential?.isEncampOwned ?? false,
  };
}

function customerDefaultValues({
  credential,
  addingCredential,
  currentFacility,
  credentialTenantId,
}: {
  credential: CustomerCredential | undefined;
  addingCredential: boolean;
  currentFacility?: InventoryOverviewQuery["facility"];
  credentialTenantId?: string | null;
}): CredentialFormValues {
  const facilityIds = credential?.facilities?.map((facility) => facility.id) ?? [];
  if (addingCredential && currentFacility) {
    facilityIds.push(currentFacility.id);
  }
  return {
    id: credential?.id ?? undefined,
    name: credential?.name ?? "",
    username: credential?.username ?? "",
    password: credential?.password ?? "",
    kind: credential?.kind ?? CredentialKind.Tierii,
    email: credential?.email,
    ...customerOnlyDefaults(credential),
    facilityIds,
    tenantId: credentialTenantId,
    portalId: credential?.portalId ?? undefined,
  };
}

/**
 * What the save will derive ownership from, so the checkbox names the tenant the server will
 * pick. Mirrors `resolveCredentialTenantId`: a stored tenant wins outright, and only a
 * credential without one takes the tenant of the facilities the save leaves linked.
 */
function staffTenancy(
  credential: StaffCredential | undefined,
  rows: CredentialFacilityRow[]
): CredentialTenancy {
  if (credential?.tenantName) {
    return { name: credential.tenantName, count: 1 };
  }
  const names = new Set(
    rows.flatMap((row) =>
      row.staged !== "removed" && row.organizationName ? [row.organizationName] : []
    )
  );
  return { name: [...names][0], count: names.size };
}

/** Distinct organizations across the rows a save would leave linked. */
function staffSubtitle(rows: CredentialFacilityRow[]): string {
  const organizations = new Set(
    rows.filter((row) => row.staged !== "removed").map((row) => row.organizationName ?? "—")
  );
  // "Shared between 0 tenants" reads as a bug rather than as a credential nothing is
  // linked to yet.
  return organizations.size === 0
    ? "No linked facilities"
    : `Shared between ${organizations.size} ${pluralize("tenant", organizations.size)}`;
}

function useCredentialRead({
  surface,
  credentialId,
}: {
  surface: CredentialSurface;
  credentialId?: string;
}) {
  const navigate = useNavigate();
  const isCustomer = surface === "customer";

  const customerQuery = useQuery(GET_CREDENTIAL, {
    fetchPolicy: "cache-and-network",
    variables: { id: credentialId ?? "" },
    skip: !isCustomer || !credentialId,
  });
  const staffQuery = useQuery(CREDENTIAL_FOR_STAFF_QUERY, {
    fetchPolicy: "cache-and-network",
    variables: { id: credentialId ?? "" },
    skip: isCustomer || !credentialId,
  });

  const {
    data: customerData,
    previousData: customerPrevious,
    loading: customerLoading,
  } = customerQuery;

  useEffect(() => {
    // A non-existent id is a non-existent route: the wildcard handler sends it to the
    // NotFound page. The staff surface renders a message in place instead — its dialog sits
    // over a grid the user hasn't navigated away from.
    if (!isCustomer || !credentialId) return;
    if (!customerLoading && !customerData && !customerPrevious) navigate("/not-found");
  }, [isCustomer, credentialId, customerLoading, customerData, customerPrevious, navigate]);

  return {
    customerCredential: credentialId ? customerData?.credential : undefined,
    staffCredential: credentialId ? (staffQuery.data?.credentialForStaff ?? undefined) : undefined,
    loading: isCustomer ? customerLoading : staffQuery.loading,
    hasLoadedOnce: isCustomer ? !!customerPrevious : !!staffQuery.previousData,
  };
}

/**
 * The Type and Portal pairing. A portal belongs to one kind, so changing the kind
 * invalidates the choice and lapses the exemption that keeps an inactive or off-kind
 * stored portal in the picker's options.
 */
function usePortalSelection({
  form,
  storedKind,
  storedPortal,
}: {
  form: CredentialForm;
  storedKind?: CredentialKind | null;
  storedPortal?: string | null;
}) {
  const { setValue, watch, formState } = form;
  const watchedKind = watch("kind");
  const previousKindRef = useRef(watchedKind);
  const [selectedPortalJurisdiction, setSelectedPortalJurisdiction] = useState<string | null>(null);

  // The ref updates before the dirty guard, not inside it: it's seeded on the first render
  // while an edit still shows the add default, so the load itself changes the kind.
  // Updating inside the guard left that pending, and the first edit to any field then read
  // as a Type change and blanked a good portal.
  useEffect(() => {
    const kindChanged = previousKindRef.current !== watchedKind;
    previousKindRef.current = watchedKind;
    if (!kindChanged || !formState.isDirty) return;

    // Only dirty fields survive a `keepDirtyValuesOnReset` reset.
    setValue("portalId", null, { shouldDirty: true });
    setSelectedPortalJurisdiction(null);
  }, [watchedKind, setValue, formState.isDirty]);

  return {
    watchedKind,
    storedPortalId: watchedKind === storedKind ? storedPortal : undefined,
    selectedPortalJurisdiction,
    setSelectedPortalJurisdiction,
  };
}

function usePasswordState({
  form,
  mode,
  credentialId,
  storedPassword,
  canViewCredentials,
}: {
  form: CredentialForm;
  mode: "add" | "edit";
  credentialId?: string;
  storedPassword: string;
  canViewCredentials: boolean;
}) {
  const { register, unregister, setValue } = form;
  const [showPasswordCopyIcon, setShowPasswordCopyIcon] = useState(canViewCredentials);
  // `EditablePasswordField` tracks this too but doesn't expose it, and the copy button in
  // the label lives outside it, so the pencil's state is mirrored here.
  const [isEditingPassword, setIsEditingPassword] = useState(false);

  useEffect(() => {
    if (canViewCredentials) return;
    unregister("password");
    // These come back as `[]` for this user; echoing that on save would erase them.
    unregister("securityQuestions");
  }, [canViewCredentials, unregister]);

  useEffect(() => {
    setShowPasswordCopyIcon(canViewCredentials);
  }, [canViewCredentials]);

  useEffect(() => {
    setIsEditingPassword(false);
  }, [credentialId]);

  /**
   * Whether there's a password to keep behind the pencil. False when the stored ciphertext
   * can't be read, which by then is indistinguishable from never set — so the field takes
   * its add shape rather than a `*********` placeholder over an empty input with nothing
   * for the eye to reveal.
   */
  const hasStoredPassword = Boolean(storedPassword);
  const passwordMode = !canViewCredentials || hasStoredPassword ? mode : "add";

  // Clearing is what makes the pencil mean "replace this". Guarded because
  // `EditablePasswordField` also wires this to `onFocus`, so clicking away from a
  // half-typed replacement and back would otherwise wipe it.
  const onPasswordEdit = useCallback(() => {
    if (passwordMode !== "edit" || isEditingPassword) return;
    setIsEditingPassword(true);
    setShowPasswordCopyIcon(false);
    register("password");
    setValue("password", "", { shouldDirty: true });
  }, [passwordMode, isEditingPassword, register, setValue]);

  const onPasswordCancelEdit = useCallback(() => {
    setIsEditingPassword(false);
    setShowPasswordCopyIcon(canViewCredentials);
    unregister("password");
    setValue("password", storedPassword, { shouldDirty: true });
  }, [canViewCredentials, unregister, setValue, storedPassword]);

  return {
    passwordMode,
    passwordHelperText:
      canViewCredentials && mode === "edit" && !hasStoredPassword
        ? "No password available — enter one to set it."
        : undefined,
    showPasswordCopyIcon,
    onPasswordEdit,
    onPasswordCancelEdit,
  };
}

/**
 * ViewCredentials (or staff) reveals the password and security answers; WriteCredential (or
 * staff) gates editing. Independent: a user can reveal on a read-only dialog. Only staff
 * reach the staff surface at all, so both are implied there.
 */
function useCredentialPermissions(isCustomer: boolean) {
  const { isStaff } = useCurrentUser();
  const { hasPermissions } = useAuthorization();

  return {
    canViewCredentials: !isCustomer || hasPermissions([Permission.ViewCredentials]) || isStaff,
    canEditCredentials: !isCustomer || hasPermissions([Permission.WriteCredential]) || isStaff,
  };
}

function useCredentialDefaults({
  isCustomer,
  staffCredential,
  customerCredential,
  addingCredential,
  currentFacility,
  credentialTenantId,
}: {
  isCustomer: boolean;
  staffCredential: StaffCredential | undefined;
  customerCredential: CustomerCredential | undefined;
  addingCredential: boolean;
  currentFacility?: InventoryOverviewQuery["facility"];
  credentialTenantId?: string | null;
}) {
  return useMemo(() => {
    if (!isCustomer) return staffDefaultValues(staffCredential);
    return customerDefaultValues({
      credential: customerCredential,
      addingCredential,
      currentFacility,
      credentialTenantId,
    });
  }, [
    isCustomer,
    staffCredential,
    customerCredential,
    addingCredential,
    currentFacility,
    credentialTenantId,
  ]);
}

function useCredentialValidation({
  addingCredential,
  storedPassword,
  isCustomer,
}: {
  addingCredential: boolean;
  storedPassword: string;
  isCustomer: boolean;
}) {
  /**
   * The field holds the stored password so the eye has something to reveal, so "changed?"
   * is a comparison against it rather than a read of the pencil — right for both cancel and
   * retype-the-same. An unchanged edit sends "", which the validator substitutes the stored
   * value for. Also keeps the plaintext off the 500ms debounced validation and stops an
   * unrelated edit re-encrypting an untouched secret.
   */
  const passwordForServer = useCallback(
    (typed: string | null | undefined) => {
      const value = typed ?? "";
      if (addingCredential) return value;
      return value === storedPassword ? "" : value;
    },
    [addingCredential, storedPassword]
  );

  const validateCredentialInput = useCredentialInputValidation(addingCredential);
  const validateInput = useCallback(
    (values: CredentialFormValues) =>
      validateCredentialInput({
        ...values,
        password: passwordForServer(values.password),
        // Stated rather than inferred from the tenant, which is briefly undefined on the
        // customer surface before its query lands and used to flip the rule set mid-edit.
        surface: isCustomer ? CredentialEditorSurface.Customer : CredentialEditorSurface.Staff,
      }),
    [validateCredentialInput, passwordForServer, isCustomer]
  );

  return { passwordForServer, validateInput };
}

export function useCredentialEditor({
  surface,
  mode,
  credentialId,
  tenantId: tenantIdProp,
  currentFacility,
  onSaved,
}: {
  surface: CredentialSurface;
  mode: "add" | "edit";
  credentialId?: string;
  /** Customer surface: overrides the tenant derived from the route. */
  tenantId?: string;
  /** Customer add mode: seeds the facility list with the facility in context. */
  currentFacility?: InventoryOverviewQuery["facility"];
  onSaved: () => void;
}) {
  const { isStaff } = useCurrentUser();
  const { tenantId: tenantFromContext } = useTenant();
  const tenantId = tenantIdProp ?? tenantFromContext;
  const isCustomer = surface === "customer";
  const addingCredential = mode === "add";

  const { customerCredential, staffCredential, loading, hasLoadedOnce } = useCredentialRead({
    surface,
    credentialId,
  });

  const active = isCustomer ? customerCredential : staffCredential;
  const storedPassword = active?.password ?? "";

  /**
   * The credential's own tenant on update, the session's only on create. Both write guards
   * reject an input whose tenant differs from the stored one, so substituting the session
   * tenant denied every save of a shared (null-tenant) credential.
   */
  const credentialTenantId = credentialId ? customerCredential?.tenantId : tenantId;

  const { canViewCredentials, canEditCredentials } = useCredentialPermissions(isCustomer);

  const { passwordForServer, validateInput } = useCredentialValidation({
    addingCredential,
    storedPassword,
    isCustomer,
  });

  const defaultValues = useCredentialDefaults({
    isCustomer,
    staffCredential,
    customerCredential,
    addingCredential,
    currentFacility,
    credentialTenantId,
  });

  const form = useValidatingForm<CredentialFormValues>(
    defaultValues,
    customerCredential?.issues ?? [],
    validateInput,
    undefined,
    { keepDirtyValuesOnReset: true }
  );

  const portal = usePortalSelection({
    form,
    storedKind: active?.kind,
    storedPortal: active?.portalId,
  });
  const password = usePasswordState({
    form,
    mode,
    credentialId,
    storedPassword,
    canViewCredentials,
  });
  const facilities = useCredentialFacilities({ form, isCustomer, staffCredential, tenantId });
  const writes = useCredentialWrites({
    form,
    isCustomer,
    addingCredential,
    credentialId,
    credentialTenantId,
    passwordForServer,
    validateInput,
    clearStaged: facilities.clearStaged,
    onSaved,
  });

  return {
    form,
    isStaff,
    tenantId,
    loading,
    hasLoadedOnce,
    readOnly: !canEditCredentials,
    canViewCredentials,
    issues: form.issues,
    notFound: !isCustomer && !loading && !staffCredential && !addingCredential,
    customerCredential,
    staffCredential,
    subtitle: isCustomer || !staffCredential ? "" : staffSubtitle(facilities.facilityRows),
    tenancy: staffTenancy(staffCredential, facilities.facilityRows),
    passwordCopyValue: storedPassword || null,
    ...portal,
    ...password,
    ...facilities,
    ...writes,
    facilitiesLoading: isCustomer ? facilities.facilitiesLoading : loading,
  };
}
